import React, { useMemo, useState, useEffect } from 'react';
import { Card } from '../../components/ui/Card';
import { LineChart } from '../../components/charts';
import { RefreshCw, Calendar, ShoppingCart, Package, BarChart2 } from 'lucide-react';

const formatCurrency = (v) => {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

function toYmdLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatMonthLabel(ymd) {
  try {
    const d = new Date(ymd);
    return d.toLocaleString(undefined, { month: 'long', year: 'numeric' });
  } catch {
    return ymd;
  }
}

function formatShortNumber(value) {
  const n = Number(value || 0);
  const abs = Math.abs(n);
  if (abs >= 1_000_000) {
    const v = n / 1_000_000;
    const s = abs < 10_000_000 ? v.toFixed(1) : Math.round(v).toString();
    return `${s}M`;
  }
  if (abs >= 1_000) {
    const v = n / 1_000;
    const s = abs < 10_000 ? v.toFixed(1) : Math.round(v).toString();
    return `${s}K`;
  }
  return n.toString();
}

function computeIsoWeekParts(ymd) {
  // ymd: 'YYYY-MM-DD'
  const d = new Date(ymd + 'T00:00:00'); // avoid timezone shift
  // ISO weekday: Monday=1..Sunday=7
  const isoDow = ((d.getDay() + 6) % 7) + 1;
  // Shift date to Thursday in current week to get ISO year
  const thursday = new Date(d);
  thursday.setDate(d.getDate() + (4 - isoDow));
  const isoYear = thursday.getFullYear();
  // Week number: weeks since first Thursday of ISO year
  const jan1 = new Date(isoYear, 0, 1);
  const jan1IsoDow = ((jan1.getDay() + 6) % 7) + 1;
  const firstThursday = new Date(jan1);
  firstThursday.setDate(jan1.getDate() + (4 - jan1IsoDow));
  const diffDays = Math.floor((thursday - firstThursday) / 86400000);
  const isoWeek = Math.floor(diffDays / 7) + 1;
  return { isoYear, isoWeek, isoDow };
}

function MiniLineChart({ data, height = 220, color = '#0ea5e9' }) {
  if (!Array.isArray(data) || data.length === 0) {
    return <div className="text-sm text-gray-500">No chart data.</div>;
  }
  const padding = { top: 10, right: 10, bottom: 24, left: 10 };
  const innerH = height - padding.top - padding.bottom;
  const step = Math.max(26, Math.floor(760 / Math.max(1, data.length - 1)));
  const width = padding.left + padding.right + step * Math.max(1, data.length - 1);
  const values = data.map(d => d.value);
  const max = Math.max(1, ...values);
  const min = 0;
  const yScale = (v) => {
    const t = (v - min) / (max - min || 1);
    return padding.top + (1 - t) * innerH;
  };
  const xScale = (i) => padding.left + i * step;
  const path = data
    .map((d, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(d.value)}`)
    .join(' ');
  const area = `${path} L ${xScale(data.length - 1)} ${yScale(min)} L ${xScale(0)} ${yScale(min)} Z`;
  const tickCount = Math.min(6, data.length);
  const tickIdx = new Set(
    Array.from({ length: tickCount }, (_, i) => Math.round((i * (data.length - 1)) / (tickCount - 1)))
  );
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id="ikasGradArea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <line x1={padding.left} y1={yScale(0)} x2={width - padding.right} y2={yScale(0)} stroke="#e5e7eb" />
      <path d={area} fill="url(#ikasGradArea)" />
      <path d={path} fill="none" stroke={color} strokeWidth="2.5" />
      {data.map((d, i) => (
        <circle key={i} cx={xScale(i)} cy={yScale(d.value)} r="2.2" fill={color} />
      ))}
      {data.map((d, i) =>
        tickIdx.has(i) ? (
          <g key={`t-${i}`}>
            <line x1={xScale(i)} y1={height - padding.bottom} x2={xScale(i)} y2={height - padding.bottom + 4} stroke="#9ca3af" />
            <text x={xScale(i)} y={height - 4} fontSize="10" textAnchor="middle" fill="#6b7280">
              {d.label.slice(5)}
            </text>
          </g>
        ) : null
      )}
    </svg>
  );
}

export default function IkasOverview() {
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 6); // default 7 days (inclusive)
    return toYmdLocal(d);
  });
  const [endDate, setEndDate] = useState(() => toYmdLocal(new Date()));
  const [metric, setMetric] = useState('revenue'); // 'revenue' | 'orders' | 'items'
  const [range, setRange] = useState('7d'); // default selected quick range
  const [loading, setLoading] = useState(false);
  const [dailyRows, setDailyRows] = useState([]);
  const [channelRows, setChannelRows] = useState([]);
  const [dailyByChannelRows, setDailyByChannelRows] = useState([]);
  const [monthlyByChannelAll, setMonthlyByChannelAll] = useState([]);
  const [channelMetric, setChannelMetric] = useState('revenue'); // 'revenue' | 'orders' | 'items'
  const [weekdayByWeek, setWeekdayByWeek] = useState([]);
  const [weeklySummary, setWeeklySummary] = useState([]);
  const [weeklyMetric, setWeeklyMetric] = useState('revenue'); // 'revenue' | 'orders' | 'items'
  const [dailyChannelRows, setDailyChannelRows] = useState([]);
  const [dailyChannelRowsAll, setDailyChannelRowsAll] = useState([]);
  const [weekdayChannel, setWeekdayChannel] = useState('');

  // Line chart data mapped from API
  const lineData = useMemo(() => {
    return (dailyRows || []).map(r => {
      const label = String(r.day || r.date || r.order_date || '').slice(0, 10);
      const value =
        metric === 'revenue'
          ? Number(r.revenue || 0)
          : metric === 'orders'
          ? Number(r.orders_count || 0)
          : Number(r.items_count || 0);
      return { label, value };
    });
  }, [dailyRows, metric]);

  const totalRevenue = (dailyRows || []).reduce((a, r) => a + Number(r.revenue || 0), 0);
  const totalOrders = (dailyRows || []).reduce((a, r) => a + Number(r.orders_count || 0), 0);
  const totalItems = (dailyRows || []).reduce((a, r) => a + Number(r.items_count || 0), 0);
  const aov = totalOrders > 0 ? totalRevenue / totalOrders : 0;

  // Build multi-series datasets for channel breakdown over days (top 6 channels)
  const channelSeries = useMemo(() => {
    const rows = dailyByChannelRows || [];
    if (rows.length === 0) return { labels: [], datasets: [] };
    // Collect labels (days)
    const labelSet = new Set(rows.map(r => String(r.day).slice(0, 10)));
    const labels = Array.from(labelSet).sort();
    // Aggregate totals per channel for ranking
    const totalsByChannel = {};
    for (const r of rows) {
      const name = r.sales_channel_name || 'Unknown';
      if (!totalsByChannel[name]) totalsByChannel[name] = { revenue: 0, orders: 0, items: 0 };
      totalsByChannel[name].revenue += Number(r.revenue || 0);
      totalsByChannel[name].orders += Number(r.orders_count || 0);
      totalsByChannel[name].items += Number(r.items_count || 0);
    }
    const rankKey = metric === 'revenue' ? 'revenue' : metric === 'orders' ? 'orders' : 'items';
    const topChannels = Object.keys(totalsByChannel)
      .sort((a, b) => totalsByChannel[b][rankKey] - totalsByChannel[a][rankKey])
      .slice(0, 6);
    const palette = [
      { stroke: 'rgb(37, 99, 235)', fill: 'rgba(37, 99, 235, 0.10)' },     // blue
      { stroke: 'rgb(16, 185, 129)', fill: 'rgba(16, 185, 129, 0.10)' },   // emerald
      { stroke: 'rgb(139, 92, 246)', fill: 'rgba(139, 92, 246, 0.10)' },   // purple
      { stroke: 'rgb(234, 88, 12)', fill: 'rgba(234, 88, 12, 0.10)' },     // orange
      { stroke: 'rgb(190, 24, 93)', fill: 'rgba(190, 24, 93, 0.10)' },     // pink
      { stroke: 'rgb(13, 148, 136)', fill: 'rgba(13, 148, 136, 0.10)' }    // teal
    ];
    const datasets = topChannels.map((ch, idx) => {
      const perDay = new Map(
        rows
          .filter(r => (r.sales_channel_name || 'Unknown') === ch)
          .map(r => [String(r.day).slice(0, 10),
            metric === 'revenue' ? Number(r.revenue || 0)
            : metric === 'orders' ? Number(r.orders_count || 0)
            : Number(r.items_count || 0)
          ])
      );
      return {
        label: ch,
        data: labels.map(d => perDay.get(d) ?? 0),
        borderColor: palette[idx % palette.length].stroke,
        backgroundColor: palette[idx % palette.length].fill,
        fill: true,
        tension: 0.4
      };
    });
    return { labels, datasets };
  }, [dailyByChannelRows, metric]);

  // Pivot all-time monthly-by-channel into columns=months, rows=channels, values by selected metric
  const channelPivot = useMemo(() => {
    const rows = monthlyByChannelAll || [];
    if (rows.length === 0) return { months: [], channels: [], valuesByChannel: {} };
    const monthSet = new Set(rows.map(r => String(r.month).slice(0, 10)));
    const months = Array.from(monthSet).sort();
    const totals = {};
    const valuesByChannel = {};
    for (const r of rows) {
      const ch = r.sales_channel_name || 'Unknown';
      const m = String(r.month).slice(0, 10);
      const val = channelMetric === 'revenue'
        ? Number(r.revenue || 0)
        : channelMetric === 'orders'
        ? Number(r.orders_count || 0)
        : Number(r.items_count || 0);
      if (!valuesByChannel[ch]) valuesByChannel[ch] = {};
      valuesByChannel[ch][m] = (valuesByChannel[ch][m] || 0) + val;
      totals[ch] = (totals[ch] || 0) + val;
    }
    const channels = Object.keys(valuesByChannel).sort((a, b) => (totals[b] || 0) - (totals[a] || 0) || a.localeCompare(b));
    return { months, channels, valuesByChannel };
  }, [monthlyByChannelAll, channelMetric]);

  // Channel type badge. Marketplace orders (Trendyol etc.) arrive on an ikas APP
  // channel named after the merchant's own store, so without this they read as
  // web store sales; the backend resolves the marketplace name for the label.
  const channelTypeByName = useMemo(() => {
    const map = {};
    for (const r of monthlyByChannelAll || []) {
      const ch = r.sales_channel_name || 'Unknown';
      if (r.channel_type && !map[ch]) map[ch] = String(r.channel_type);
    }
    return map;
  }, [monthlyByChannelAll]);

  const channelTypeBadge = (type) => {
    switch (String(type || '').toUpperCase()) {
      case 'APP': return { text: 'Marketplace', cls: 'bg-orange-100 text-orange-700' };
      case 'STOREFRONT': return { text: 'Web store', cls: 'bg-blue-100 text-blue-700' };
      case 'POS': return { text: 'POS', cls: 'bg-emerald-100 text-emerald-700' };
      case 'ADMIN': return { text: 'Manual', cls: 'bg-gray-100 text-gray-600' };
      case 'B2B': return { text: 'B2B', cls: 'bg-purple-100 text-purple-700' };
      default: return null;
    }
  };

  // Fetch data from backend endpoints
  const fetchIkasData = async (overrideStart, overrideEnd) => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        console.warn('No brand selected');
        setDailyRows([]);
        setChannelRows([]);
        setDailyByChannelRows([]);
        return;
      }
      setLoading(true);
      // Demo fallback for specific brand (skip all network requests)
      const DEMO_BRAND_ID = 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4';
      if (brandId === DEMO_BRAND_ID) {
        const s = overrideStart || startDate;
        const e = overrideEnd || endDate;
        const start = s ? new Date(`${s}T00:00:00`) : new Date(new Date().setDate(new Date().getDate() - 6));
        const end = e ? new Date(`${e}T00:00:00`) : new Date();
        // Build selected-range days
        const days = [];
        for (let d = new Date(start); d <= end; d = new Date(d.getTime() + 86400000)) {
          days.push(d.toISOString().slice(0, 10));
        }
        // Daily totals
        const daily = days.map((d, i) => ({
          day: d,
          revenue: 42000 + Math.round(12000 * Math.sin(i / 3)) + (i % 5) * 1600,
          orders_count: 210 + (i % 7) * 9 + Math.round(16 * Math.cos(i / 4)),
          items_count: 520 + (i % 6) * 11 + Math.round(24 * Math.sin(i / 5))
        }));
        setDailyRows(daily);
        // Channels
        const channels = [
          { name: 'Online Store', frac: 0.60 },
          { name: 'Marketplace', frac: 0.30 },
          { name: 'POS', frac: 0.10 }
        ];
        // Daily by channel (selected range)
        const dailyByCh = [];
        for (let i = 0; i < days.length; i++) {
          const d = days[i];
          const baseRev = Number(daily[i]?.revenue || 0);
          const baseOrd = Number(daily[i]?.orders_count || 0);
          const baseItm = Number(daily[i]?.items_count || 0);
          channels.forEach((ch, idx) => {
            const f = ch.frac * (1 + 0.05 * Math.sin((i + idx) / 2));
            dailyByCh.push({
              day: d,
              sales_channel_name: ch.name,
              revenue: Math.max(0, Math.round(baseRev * f)),
              orders_count: Math.max(0, Math.round(baseOrd * f)),
              items_count: Math.max(0, Math.round(baseItm * f))
            });
          });
        }
        setDailyByChannelRows(dailyByCh);
        // All-time daily-by-channel view (last ~90 days)
        const allDays = [];
        const today = new Date();
        const startAll = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 89);
        for (let d = new Date(startAll); d <= today; d = new Date(d.getTime() + 86400000)) {
          allDays.push(d.toISOString().slice(0, 10));
        }
        const dailyChAll = [];
        for (let i = 0; i < allDays.length; i++) {
          const d = allDays[i];
          const baseRev = 38000 + Math.round(14000 * Math.sin(i / 3)) + (i % 5) * 1200;
          const baseOrd = 190 + (i % 7) * 7 + Math.round(14 * Math.cos(i / 4));
          const baseItm = 470 + (i % 6) * 10 + Math.round(20 * Math.sin(i / 5));
          channels.forEach((ch, idx) => {
            const f = ch.frac * (1 + 0.05 * Math.sin((i + idx) / 3));
            dailyChAll.push({
              day: d,
              sales_channel_name: ch.name,
              revenue: Math.max(0, Math.round(baseRev * f)),
              orders_count: Math.max(0, Math.round(baseOrd * f)),
              items_count: Math.max(0, Math.round(baseItm * f))
            });
          });
        }
        setDailyChannelRows(dailyByCh);
        setDailyChannelRowsAll(dailyChAll);
        // Weekday by ISO week (derive from all-time channel rows)
        const weekdayRows = [];
        for (const r of dailyChAll) {
          const ymd = String(r.day).slice(0, 10);
          const parts = computeIsoWeekParts(ymd);
          weekdayRows.push({
            iso_year: parts.isoYear,
            iso_week: parts.isoWeek,
            iso_dow: parts.isoDow,
            day: ymd,
            orders_count: Number(r.orders_count || 0),
            items_count: Number(r.items_count || 0),
            revenue: Number(r.revenue || 0),
          });
        }
        setWeekdayByWeek(weekdayRows);
        // Monthly by channel (last 6 months, all-time pivot)
        const months = [];
        for (let k = 5; k >= 0; k--) {
          const d = new Date(today.getFullYear(), today.getMonth() - k, 1);
          months.push(d.toISOString().slice(0, 10));
        }
        const monthlyAll = [];
        months.forEach((m, mi) => {
          channels.forEach((ch, ci) => {
            const mBase = 950000 + mi * 50000 + ci * 40000;
            monthlyAll.push({
              month: m,
              sales_channel_name: ch.name,
              revenue: mBase,
              orders_count: 4800 + mi * 220 + ci * 180,
              items_count: 11200 + mi * 480 + ci * 360
            });
          });
        });
        // Ensure sorted
        monthlyAll.sort((a, b) => String(a.month).localeCompare(String(b.month)) || String(a.sales_channel_name || '').localeCompare(String(b.sales_channel_name || '')));
        setMonthlyByChannelAll(monthlyAll);
        // Aggregate to totals per channel for channelRows
        const agg = {};
        for (const r of monthlyAll) {
          const k = r.sales_channel_name || 'Unknown';
          if (!agg[k]) agg[k] = { channel: k, orders: 0, items: 0, revenue: 0 };
          agg[k].orders += Number(r.orders_count || 0);
          agg[k].items += Number(r.items_count || 0);
          agg[k].revenue += Number(r.revenue || 0);
        }
        setChannelRows(Object.values(agg));
        setLoading(false);
        return;
      }
      const params = new URLSearchParams();
      const s = overrideStart || startDate;
      const e = overrideEnd || endDate;
      if (s) params.set('startDate', s);
      if (e) params.set('endDate', e);
      const headers = { 'X-Brand-ID': brandId };
      const base = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? '' : 'https://api.glowytics.com';
      // Daily series
      const dailyRes = await fetch(`${base}/api/ikas/orders/daily?${params.toString()}`, {
        credentials: 'include',
        headers
      });
      const dailyJson = await dailyRes.json().catch(() => ({}));
      setDailyRows(dailyRes.ok && dailyJson?.rows ? dailyJson.rows : []);
      // Channel monthly -> reduce to totals
      const chanRes = await fetch(`${base}/api/ikas/orders/monthly-by-channel?${params.toString()}`, {
        credentials: 'include',
        headers
      });
      const chanJson = await chanRes.json().catch(() => ({}));
      const monthly = chanRes.ok && chanJson?.rows ? chanJson.rows : [];
      const agg = {};
      for (const r of monthly) {
        const k = r.sales_channel_name || 'Unknown';
        if (!agg[k]) agg[k] = { channel: k, orders: 0, items: 0, revenue: 0 };
        agg[k].orders += Number(r.orders_count || 0);
        agg[k].items += Number(r.items_count || r.items || 0);
        agg[k].revenue += Number(r.revenue || 0);
      }
      setChannelRows(Object.values(agg));
      // Daily by channel
      const dailyChanRes = await fetch(`${base}/api/ikas/orders/daily-by-channel?${params.toString()}`, {
        credentials: 'include',
        headers
      });
      const dailyChanJson = await dailyChanRes.json().catch(() => ({}));
      setDailyByChannelRows(dailyChanRes.ok && dailyChanJson?.rows ? dailyChanJson.rows : []);
      // All-time monthly by channel (no date filters)
      const allMonthlyRes = await fetch(`${base}/api/ikas/orders/monthly-by-channel`, {
        credentials: 'include',
        headers
      });
      const allMonthlyJson = await allMonthlyRes.json().catch(() => ({}));
      const allRows = allMonthlyRes.ok && allMonthlyJson?.rows ? allMonthlyJson.rows : [];
      // sort by month then channel
      allRows.sort((a, b) => String(a.month).localeCompare(String(b.month)) || String(a.sales_channel_name || '').localeCompare(String(b.sales_channel_name || '')));
      setMonthlyByChannelAll(allRows);
      // Weekday by ISO week (within selected range)
      let wdwRes = await fetch(`${base}/api/ikas/orders/weekday-by-iso-week?${params.toString()}`, {
        credentials: 'include',
        headers
      });
      let wdwJson = await wdwRes.json().catch(() => ({}));
      let wdwRows = wdwRes.ok && wdwJson?.rows ? wdwJson.rows : [];
      // Fallback to all available if range returns empty
      if (!Array.isArray(wdwRows) || wdwRows.length === 0) {
        wdwRes = await fetch(`${base}/api/ikas/orders/weekday-by-iso-week`, {
          credentials: 'include',
          headers
        });
        wdwJson = await wdwRes.json().catch(() => ({}));
        wdwRows = wdwRes.ok && wdwJson?.rows ? wdwJson.rows : [];
      }
      setWeekdayByWeek(wdwRows);
      // Weekly summary will be computed client-side from all-time daily channel rows (see effect below)
      // Daily channel view (selected range)
      const dcvRes = await fetch(`${base}/api/ikas/orders/daily-by-channel-view?${params.toString()}`, {
        credentials: 'include',
        headers
      });
      const dcvJson = await dcvRes.json().catch(() => ({}));
      setDailyChannelRows(dcvRes.ok && dcvJson?.rows ? dcvJson.rows : []);
      // Daily channel view (all available; not affected by header filter)
      const dcvAllRes = await fetch(`${base}/api/ikas/orders/daily-by-channel-view`, {
        credentials: 'include',
        headers
      });
      const dcvAllJson = await dcvAllRes.json().catch(() => ({}));
      setDailyChannelRowsAll(dcvAllRes.ok && dcvAllJson?.rows ? dcvAllJson.rows : []);
    } catch (e) {
      console.error('IKAS fetch error', e);
      setDailyRows([]);
      setChannelRows([]);
      setDailyByChannelRows([]);
      setMonthlyByChannelAll([]);
      setWeekdayByWeek([]);
      setDailyChannelRowsAll([]);
      setWeeklySummary([]);
    } finally {
      setLoading(false);
    }
  };

  // Quick range: set dates and fetch immediately
  const applyQuickRange = (id) => {
    const today = new Date();
    let s = new Date(today);
    let e = new Date(today);
    switch (id) {
      case 'yesterday':
        s.setDate(today.getDate() - 1);
        e.setDate(today.getDate() - 1);
        break;
      case '7d':
        s.setDate(today.getDate() - 6);
        break;
      case '30d':
        s.setDate(today.getDate() - 29);
        break;
      case 'thisMonth':
        s = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'lastMonth': {
        const firstOfThis = new Date(today.getFullYear(), today.getMonth(), 1);
        e = new Date(firstOfThis.getTime() - 86400000);
        s = new Date(e.getFullYear(), e.getMonth(), 1);
        break;
      }
      case 'all':
        s = new Date(today.getFullYear() - 3, 0, 1);
        break;
      default:
        break;
    }
    const ns = toYmdLocal(s);
    const ne = toYmdLocal(e);
    setRange(id);
    setStartDate(ns);
    setEndDate(ne);
    fetchIkasData(ns, ne);
  };

  useEffect(() => {
    fetchIkasData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Available channels list (from current range totals)
  const availableChannels = useMemo(() => {
    const set = new Set((dailyChannelRowsAll || dailyChannelRows || []).map(r => r.sales_channel_name || 'Unknown'));
    const arr = Array.from(set);
    arr.sort();
    return ['All', ...arr];
  }, [dailyChannelRowsAll, dailyChannelRows]);

  // Default channel pick when channels list loads
  useEffect(() => {
    if (!weekdayChannel && availableChannels.length > 0) {
      setWeekdayChannel(availableChannels[0]); // default: All
    }
  }, [availableChannels, weekdayChannel]);

  // Build weekday-by-week matrix from already-fetched dailyChannelRows (client-side aggregate)
  useEffect(() => {
    try {
      if (!weekdayChannel) { setWeekdayByWeek([]); return; }
      const src = (dailyChannelRowsAll && dailyChannelRowsAll.length > 0) ? dailyChannelRowsAll : dailyChannelRows;
      const filtered = (src || []).filter(r => {
        const ch = (r.sales_channel_name || 'Unknown');
        return weekdayChannel === 'All' ? true : (ch === weekdayChannel);
      });
      const out = [];
      for (const r of filtered) {
        const ymd = String(r.day).slice(0, 10);
        const parts = computeIsoWeekParts(ymd);
        out.push({
          iso_year: parts.isoYear,
          iso_week: parts.isoWeek,
          iso_dow: parts.isoDow,
          day: ymd,
          orders_count: Number(r.orders_count || 0),
          items_count: Number(r.items_count || 0),
          revenue: Number(r.revenue || 0),
        });
      }
      setWeekdayByWeek(out);
    } catch (e) {
      console.error('build weekday matrix error', e);
      setWeekdayByWeek([]);
    }
  }, [weekdayChannel, dailyChannelRows, dailyChannelRowsAll]);

  // Build Week-over-Week Summary from all-time daily channel rows (all channels)
  useEffect(() => {
    try {
      const src = (dailyChannelRowsAll && dailyChannelRowsAll.length > 0) ? dailyChannelRowsAll : dailyChannelRows;
      if (!src || src.length === 0) { setWeeklySummary([]); return; }
      // Aggregate per ISO week, filtered by channel if not 'All'
      const agg = new Map();
      for (const r of src) {
        const ch = (r.sales_channel_name || 'Unknown');
        if (weekdayChannel && weekdayChannel !== 'All' && ch !== weekdayChannel) continue;
        const ymd = String(r.day).slice(0, 10);
        const parts = computeIsoWeekParts(ymd);
        const key = `${parts.isoYear}-${String(parts.isoWeek).padStart(2, '0')}`;
        if (!agg.has(key)) {
          agg.set(key, {
            iso_year: parts.isoYear,
            iso_week: parts.isoWeek,
            revenue: 0, orders_count: 0, items_count: 0
          });
        }
        const row = agg.get(key);
        row.revenue += Number(r.revenue || 0);
        row.orders_count += Number(r.orders_count || 0);
        row.items_count += Number(r.items_count || 0);
      }
      const weeks = Array.from(agg.values()).sort((a, b) => a.iso_year - b.iso_year || a.iso_week - b.iso_week);
      // Compute WoW pct deltas
      for (let i = 0; i < weeks.length; i++) {
        const prev = i > 0 ? weeks[i - 1] : null;
        const cur = weeks[i];
        const pct = (curVal, prevVal) => (prevVal && prevVal !== 0) ? ((curVal - prevVal) / prevVal) * 100 : null;
        cur.revenue_wow_pct = pct(cur.revenue, prev?.revenue || 0);
        cur.orders_wow_pct = pct(cur.orders_count, prev?.orders_count || 0);
        cur.items_wow_pct = pct(cur.items_count, prev?.items_count || 0);
      }
      setWeeklySummary(weeks.reverse()); // newest first
    } catch (e) {
      console.error('build weekly summary error', e);
      setWeeklySummary([]);
    }
  }, [dailyChannelRowsAll, dailyChannelRows, weekdayChannel]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-3">
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-3">
            <img 
              src="/ikaslogoicon.png" 
              alt="Ikas Logo" 
              className="w-10 h-10 object-contain rounded-lg"
            />
            <div>
              <h1 className="text-xl font-bold text-gray-900">ikas Overview</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Date Filter Buttons */}
            <div className="flex items-center gap-1 bg-gray-50 p-0.5 rounded-lg">
              {[
                { id: 'yesterday', label: 'Yesterday' },
                { id: '7d', label: '7 Days' },
                { id: '30d', label: '30 Days' },
                { id: 'thisMonth', label: 'This Month' },
                { id: 'lastMonth', label: 'Last Month' },
                { id: 'all', label: 'All Time' }
              ].map(opt => (
                <button
                  key={opt.id}
                  className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                    range === opt.id ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-600 hover:bg-white hover:text-gray-900'
                  }`}
                  onClick={() => applyQuickRange(opt.id)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            
            <button
              className="p-2 text-gray-600 hover:text-gray-900 rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-60"
              onClick={() => fetchIkasData()}
              disabled={loading}
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

      </div>

      {/* Filters removed; header quick filters are used */}

      {/* KPIs */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Revenue */}
          <Card className="p-4 rounded-xl shadow-xl border bg-white border-gray-100 hover:bg-blue-50 transition-colors cursor-default">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-gray-500">Total Revenue</p>
                <p className="text-lg font-semibold mt-1">₺{formatCurrency(totalRevenue)}</p>
              </div>
              <div className="p-2 rounded-lg bg-blue-50">
                <ShoppingCart className="w-4 h-4 text-blue-600" />
              </div>
            </div>
          </Card>
          {/* Orders */}
          <Card className="p-4 rounded-xl shadow-xl border bg-white border-gray-100 hover:bg-indigo-50 transition-colors cursor-default">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-gray-500">Total Orders</p>
                <p className="text-lg font-semibold mt-1">{totalOrders.toLocaleString()}</p>
              </div>
              <div className="p-2 rounded-lg bg-indigo-50">
                <BarChart2 className="w-4 h-4 text-indigo-600" />
              </div>
            </div>
          </Card>
          {/* Items */}
          <Card className="p-4 rounded-xl shadow-xl border bg-white border-gray-100 hover:bg-amber-50 transition-colors cursor-default">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-gray-500">Total Items</p>
                <p className="text-lg font-semibold mt-1">{totalItems.toLocaleString()}</p>
              </div>
              <div className="p-2 rounded-lg bg-amber-50">
                <Package className="w-4 h-4 text-amber-600" />
              </div>
            </div>
          </Card>
          {/* AOV */}
          <Card className="p-4 rounded-xl shadow-xl border bg-white border-gray-100 hover:bg-emerald-50 transition-colors cursor-default">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-gray-500">Average Order Value</p>
                <p className="text-lg font-semibold mt-1">₺{formatCurrency(aov)}</p>
              </div>
              <div className="p-2 rounded-lg bg-emerald-50">
                <BarChart2 className="w-4 h-4 text-emerald-600" />
              </div>
            </div>
          </Card>
        </div>
        <div className="flex items-center justify-between mt-5 mb-3">
          <div className="text-sm font-medium text-gray-800">Daily Trend</div>
          <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
            <button
              className={`px-2.5 py-1.5 rounded-lg text-xs ${metric === 'revenue' ? 'bg-white shadow-sm text-emerald-700' : 'text-gray-700 hover:text-gray-900'}`}
              onClick={() => setMetric('revenue')}
            >
              Revenue
            </button>
            <button
              className={`px-2.5 py-1.5 rounded-lg text-xs ${metric === 'orders' ? 'bg-white shadow-sm text-blue-700' : 'text-gray-700 hover:text-gray-900'}`}
              onClick={() => setMetric('orders')}
            >
              Orders
            </button>
            <button
              className={`px-2.5 py-1.5 rounded-lg text-xs ${metric === 'items' ? 'bg-white shadow-sm text-amber-700' : 'text-gray-700 hover:text-gray-900'}`}
              onClick={() => setMetric('items')}
            >
              Items
            </button>
          </div>
        </div>
        <div className="h-72">
          {channelSeries.datasets.length > 0 ? (
            <LineChart
              data={{
                labels: channelSeries.labels,
                datasets: channelSeries.datasets
              }}
            />
          ) : lineData.length > 0 ? (
            <LineChart 
              data={{
                labels: lineData.map(d => d.label),
                datasets: [{
                  label: metric === 'revenue' ? 'Revenue' : metric === 'orders' ? 'Orders' : 'Items',
                  data: lineData.map(d => d.value),
                  borderColor: metric === 'revenue' ? 'rgb(16, 185, 129)' : metric === 'orders' ? 'rgb(37, 99, 235)' : 'rgb(217, 119, 6)',
                  backgroundColor: metric === 'revenue' ? 'rgba(16, 185, 129, 0.1)' : (metric === 'orders' ? 'rgba(37, 99, 235, 0.1)' : 'rgba(217, 119, 6, 0.1)'),
                  fill: true,
                  tension: 0.4
                }]
              }}
            />
          ) : (
            <div className="text-sm text-gray-500">No chart data.</div>
          )}
        </div>
      </div>

      {/* Sales Channel Breakdown (All-time monthly by channel view, pivoted) */}
      <div className="bg-white border rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Sales Channel Performance</h2>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Metric</span>
            <select
              className="text-xs border rounded-md px-2 py-1 bg-white"
              value={channelMetric}
              onChange={(e) => setChannelMetric(e.target.value)}
            >
              <option value="revenue">Revenue</option>
              <option value="orders">Orders</option>
              <option value="items">Items</option>
            </select>
          </div>
        </div>
        {monthlyByChannelAll.length === 0 ? (
          <div className="text-sm text-gray-500">No data.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left border-b align-bottom">
                  <th className="py-2 pr-4">Sales Channel</th>
                  {channelPivot.months.map((m) => (
                    <th key={m} className="py-2 pr-4">{formatMonthLabel(m)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {channelPivot.channels.map((ch) => {
                  const badge = channelTypeBadge(channelTypeByName[ch]);
                  return (
                  <tr key={ch} className="border-b hover:bg-gray-50">
                    <td className="py-2 pr-4 font-medium">
                      <span className="inline-flex items-center gap-2">
                        {ch}
                        {badge && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-normal ${badge.cls}`}>{badge.text}</span>
                        )}
                      </span>
                    </td>
                    {channelPivot.months.map((m) => {
                      const v = channelPivot.valuesByChannel[ch]?.[m] || 0;
                      const txt = channelMetric === 'revenue' ? `${formatCurrency(v)}` : Number(v).toLocaleString();
                      return <td key={`${ch}-${m}`} className="py-2 pr-4">{txt}</td>;
                    })}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Weekly tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: Weekday Revenue by Week */}
        <div className="bg-white border rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold">Weekday by ISO Week</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Channel</span>
              <select
                className="text-xs border rounded-md px-2 py-1 bg-white"
                value={weekdayChannel}
                onChange={(e) => setWeekdayChannel(e.target.value)}
              >
                {availableChannels.map((c) => (
                  <option key={c} value={c}>{c || 'Unknown'}</option>
                ))}
              </select>
              <span className="text-xs text-gray-500">Metric</span>
              <select
                className="text-xs border rounded-md px-2 py-1 bg-white"
                value={weeklyMetric}
                onChange={(e) => setWeeklyMetric(e.target.value)}
              >
                <option value="revenue">Revenue</option>
                <option value="orders">Orders</option>
                <option value="items">Items</option>
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <div className="max-h-[360px] overflow-y-auto">
              <table className="min-w-full text-xs">
                <thead className="sticky top-0 bg-white">
                <tr className="text-left border-b">
                  <th className="py-2 pr-3">Week</th>
                  <th className="py-2 pr-3">Mon</th>
                  <th className="py-2 pr-3">Tue</th>
                  <th className="py-2 pr-3">Wed</th>
                  <th className="py-2 pr-3">Thu</th>
                  <th className="py-2 pr-3">Fri</th>
                  <th className="py-2 pr-3">Sat</th>
                  <th className="py-2 pr-3">Sun</th>
                  <th className="py-2 pr-3">Total</th>
                </tr>
                </thead>
                <tbody>
                {(() => {
                  // Build pivot: rows = year-week, cols = 1..7
                  const grouped = new Map();
                  for (const r of weekdayByWeek) {
                    const y = Number(r.iso_year);
                    const w = Number(r.iso_week);
                    const key = `${y}-W${String(w).padStart(2, '0')}`;
                    if (!grouped.has(key)) grouped.set(key, { year: y, week: w, byDow: Array(8).fill(0) });
                    const v = weeklyMetric === 'revenue' ? Number(r.revenue || 0)
                            : weeklyMetric === 'orders' ? Number(r.orders_count || 0)
                            : Number(r.items_count || 0);
                    grouped.get(key).byDow[Number(r.iso_dow)] = (grouped.get(key).byDow[Number(r.iso_dow)] || 0) + v;
                  }
                  const rows = Array.from(grouped.values())
                    .sort((a, b) => b.year - a.year || b.week - a.week);
                  if (rows.length === 0) return (
                    <tr><td className="py-3 text-gray-500" colSpan={9}>No data.</td></tr>
                  );
                  return rows.map((rw) => {
                    const total = rw.byDow.slice(1).reduce((s, x) => s + (x || 0), 0);
                    const fmt = (x) => weeklyMetric === 'revenue' ? `₺${formatShortNumber(x)}` : formatShortNumber(x);
                    return (
                      <tr key={`${rw.year}-${rw.week}`} className="border-b">
                        <td className="py-2 pr-3 font-medium">{`W${String(rw.week).padStart(2, '0')} ${rw.year}`}</td>
                        {[1,2,3,4,5,6,7].map((d) => (
                          <td key={d} className="py-2 pr-3">{fmt(rw.byDow[d] || 0)}</td>
                        ))}
                        <td className="py-2 pr-3 font-semibold">{fmt(total)}</td>
                      </tr>
                    );
                  });
                })()}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right: Week-over-Week Summary */}
        <div className="bg-white border rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="text-lg font-semibold">Week-over-Week Summary</div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Channel</span>
              <select
                className="text-xs border rounded-md px-2 py-1 bg-white"
                value={weekdayChannel}
                onChange={(e) => setWeekdayChannel(e.target.value)}
              >
                {availableChannels.map((c) => (
                  <option key={c} value={c}>{c || 'Unknown'}</option>
                ))}
              </select>
              <span className="text-xs text-gray-500">Metric</span>
              <select
                className="text-xs border rounded-md px-2 py-1 bg-white"
                value={weeklyMetric}
                onChange={(e) => setWeeklyMetric(e.target.value)}
              >
                <option value="revenue">Revenue</option>
                <option value="orders">Orders</option>
                <option value="items">Items</option>
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <div className="max-h-[360px] overflow-y-auto">
              <table className="min-w-full text-xs">
                <thead className="sticky top-0 bg-white">
                <tr className="text-left border-b">
                  <th className="py-2 pr-3">Week</th>
                  <th className="py-2 pr-3">Revenue</th>
                  <th className="py-2 pr-3">Orders</th>
                  <th className="py-2 pr-3">Items</th>
                  <th className="py-2 pr-3">Δ Rev</th>
                  <th className="py-2 pr-3">Δ Orders</th>
                  <th className="py-2 pr-3">Δ Items</th>
                </tr>
                </thead>
                <tbody>
                {weeklySummary.length === 0 ? (
                  <tr><td className="py-3 text-gray-500" colSpan={7}>No data.</td></tr>
                ) : weeklySummary.map((r, idx) => {
                  const wLabel = `W${String(r.iso_week).padStart(2, '0')} ${r.iso_year}`;
                  const fmtC = (v) => `₺${formatShortNumber(v)}`;
                  const fmtN = (v) => formatShortNumber(v);
                  const fmtPct = (v) => (v === null || v === undefined) ? '-' : `${(Number(v)).toFixed(1)}%`;
                  const color = (v) => (v === null || v === undefined) ? 'text-gray-500' : (v >= 0 ? 'text-emerald-600' : 'text-rose-600');
                  const hi = (col) => weeklyMetric === col ? 'font-semibold text-gray-900' : 'text-gray-700';
                  return (
                    <tr key={idx} className="border-b">
                      <td className="py-2 pr-3 font-medium">{wLabel}</td>
                      <td className={`py-2 pr-3 ${hi('revenue')}`}>{fmtC(r.revenue || 0)}</td>
                      <td className={`py-2 pr-3 ${hi('orders')}`}>{fmtN(r.orders_count || 0)}</td>
                      <td className={`py-2 pr-3 ${hi('items')}`}>{fmtN(r.items_count || 0)}</td>
                      <td className={`py-2 pr-3 ${color(r.revenue_wow_pct)}`}>{fmtPct(r.revenue_wow_pct)}</td>
                      <td className={`py-2 pr-3 ${color(r.orders_wow_pct)}`}>{fmtPct(r.orders_wow_pct)}</td>
                      <td className={`py-2 pr-3 ${color(r.items_wow_pct)}`}>{fmtPct(r.items_wow_pct)}</td>
                    </tr>
                  );
                })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


